import { AlertTriangle, Camera, Clock, MapPin, ShieldCheck, Video } from 'lucide-react'
import { useEffect, useState } from 'react'
import { deleteStageFile, fileDownloadURL } from '@/data/fileApi'
import type { FileRecord } from '@/data/useProjectFiles'
import { errorMessage } from '@/lib/errors'
import { formatFileSize } from '@/lib/format'
import { assessProof } from '@/lib/proof'
import type { LatLng } from '@/types/project'
import type { Role } from '@/types/user'

interface FileListProps {
  projectId: string
  files: FileRecord[]
  role: Role
  currentUid: string
  /** Render proof-of-product geolocation/metadata + authenticity flags. */
  proof?: boolean
  /** Expected route points (origin, checkpoints, delivery) for distance checks. */
  expectedPoints?: LatLng[]
}

// The proof-photo location / authenticity panel is hidden until we have a
// reliable way to capture a photo's location. Set to true to re-enable it.
const SHOW_PROOF_METADATA: boolean = false

function formatWhen(value: string): string {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

/** Thumbnail preview for an image file; opens the full image in a new tab. */
function ImageThumb({ file }: { file: FileRecord }) {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    fileDownloadURL(file.storagePath)
      .then((resolved) => {
        if (active) setUrl(resolved)
      })
      .catch(() => {
        // Keep the placeholder if the URL can't be fetched.
      })
    return () => {
      active = false
    }
  }, [file.storagePath])

  if (!url) {
    return <div className="h-14 w-14 shrink-0 animate-pulse rounded-xl bg-slate-100" aria-hidden />
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="shrink-0">
      <img
        src={url}
        alt={file.fileName}
        loading="lazy"
        className="h-14 w-14 rounded-xl border border-slate-200 object-cover shadow-sm transition hover:opacity-95 hover:shadow-md"
      />
    </a>
  )
}

function ProofMeta({ file, expectedPoints }: { file: FileRecord; expectedPoints: LatLng[] }) {
  const assessment = assessProof(file, expectedPoints)
  return (
    <div className="mt-1.5 space-y-1 rounded-md bg-slate-50 px-2 py-1.5 text-xs">
      {assessment.isVideo && (
        <p className="flex items-center gap-1 text-slate-500">
          <Video className="h-3 w-3 shrink-0" /> Video evidence
        </p>
      )}
      <p className="flex items-center gap-1 text-slate-500">
        <MapPin className="h-3 w-3 shrink-0" />
        {file.gps ? (
          <span>
            {file.gps.lat.toFixed(4)}, {file.gps.lng.toFixed(4)}
            {assessment.distanceKm != null &&
              ` · ${Math.round(assessment.distanceKm)} km from route`}
          </span>
        ) : (
          'No location data'
        )}
      </p>
      {file.takenAt && (
        <p className="flex items-center gap-1 text-slate-500">
          <Clock className="h-3 w-3 shrink-0" /> {formatWhen(file.takenAt)}
        </p>
      )}
      {file.camera && (
        <p className="flex items-center gap-1 text-slate-500">
          <Camera className="h-3 w-3 shrink-0" /> {file.camera}
        </p>
      )}
      {assessment.flags.map((flag) => (
        <p key={flag} className="flex items-start gap-1 text-amber-700">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> {flag}
        </p>
      ))}
      {file.gps && file.serverVerified ? (
        <p className="flex items-center gap-1 font-medium text-green-700">
          <ShieldCheck className="h-3 w-3 shrink-0" /> Verified from photo
        </p>
      ) : file.gps && file.locationSource === 'device' ? (
        <p className="font-medium text-blue-700">Live device location (GPS at capture)</p>
      ) : null}
    </div>
  )
}

export function FileList({
  projectId,
  files,
  role,
  currentUid,
  proof = false,
  expectedPoints = [],
}: FileListProps) {
  const [error, setError] = useState<string | null>(null)

  if (files.length === 0) {
    return <p className="text-sm text-slate-400">No files uploaded.</p>
  }

  async function download(file: FileRecord) {
    setError(null)
    try {
      const url = await fileDownloadURL(file.storagePath)
      window.open(url, '_blank', 'noopener')
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  async function remove(file: FileRecord) {
    setError(null)
    try {
      await deleteStageFile(projectId, file)
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  return (
    <div className="space-y-2">
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
        {files.map((file) => {
          const canDelete = role === 'admin' || file.uploadedByUid === currentUid
          return (
            <li key={file.id} className="px-3 py-2 text-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  {file.contentType?.startsWith('image/') && <ImageThumb file={file} />}
                  <div className="min-w-0">
                    <button
                      type="button"
                      onClick={() => download(file)}
                      className="block max-w-full truncate font-medium text-primary hover:underline"
                    >
                      {file.fileName}
                    </button>
                    <div className="text-xs text-slate-400">
                      {formatFileSize(file.size)} · {file.uploadedByName}
                    </div>
                  </div>
                </div>
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => remove(file)}
                    aria-label={`Delete ${file.fileName}`}
                    className="shrink-0 text-xs font-medium text-danger hover:underline"
                  >
                    Delete
                  </button>
                )}
              </div>
              {SHOW_PROOF_METADATA &&
                proof &&
                (file.contentType?.startsWith('image/') ||
                  file.contentType?.startsWith('video/')) && (
                  <ProofMeta file={file} expectedPoints={expectedPoints} />
                )}
            </li>
          )
        })}
      </ul>
      {error && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  )
}
